import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useUser } from 'context/user/User';
import { getReservations } from 'api/requests/reservations';
import pusherService from 'services/pusher';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';

const ReservationNotificationsContext = createContext();

export default function ReservationNotificationsProvider({ children }) {
    const { user } = useUser();
    const establishmentId = user?.establishments?.[0]?.id;
    const establishmentSlug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();
    const [reservations, setReservations] = useState([]);

    useEffect(() => {
        if (!establishmentSlug) return;
        getReservations(establishmentSlug)
            .then(setReservations)
            .catch(error => console.error('Error fetching reservations for notifications', error));
    }, [establishmentSlug]);

    const upsertReservation = useCallback((reservation) => {
        setReservations(prev => {
            const exists = prev.some(r => r.id === reservation.id);
            if (exists) return prev.map(r => r.id === reservation.id ? reservation : r);
            return [...prev, reservation];
        });
    }, []);

    useEffect(() => {
        if (!establishmentId) return;

        const channelName = `establishments.${establishmentId}`;

        const handleCreated = (reservation) => {
            upsertReservation(reservation);
            toast.success(`Nova reserva de ${reservation.person_name} para ${reservation.reservation_date} às ${reservation.reservation_time}`);
        };

        const handleStatusUpdated = (reservation) => {
            upsertReservation(reservation);
        };

        pusherService.subscribe(channelName, 'reservation.created', handleCreated);
        pusherService.subscribe(channelName, 'reservation.status_updated', handleStatusUpdated);

        return () => {
            pusherService.unsubscribe(channelName, 'reservation.created', handleCreated);
            pusherService.unsubscribe(channelName, 'reservation.status_updated', handleStatusUpdated);
        };
    }, [establishmentId, toast, upsertReservation]);

    const pendingCount = reservations.filter(r => r.status === 'pending').length;

    return (
        <ReservationNotificationsContext.Provider value={{ reservations, upsertReservation, pendingCount }}>
            <ToastContainer toasts={toasts} />
            {children}
        </ReservationNotificationsContext.Provider>
    );
}

export function useReservationNotifications() {
    const context = useContext(ReservationNotificationsContext);

    if (!context) throw new Error('useReservationNotifications must be used within a ReservationNotificationsProvider');

    return context;
}

// Safe to use outside the provider (e.g. sidebars rendered by DashContainer, which
// doesn't mount ReservationNotificationsProvider) — returns undefined instead of throwing.
export function useReservationNotificationsOptional() {
    return useContext(ReservationNotificationsContext);
}
